import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  FilterDropdown,
  IconButton,
  InputText,
  InputNumber,
  Tag,
} from '@teo/components';
import {
  Search,
  Pencil,
  ChevronUp,
  ChevronDown,
  Plus,
  ChevronDownFilled,
} from '@teo/components/icons';
import { useEffect, useState, useContext, Fragment } from 'react';
import { DataTable } from '@/components/DataTable/DataTable';
import { useGet } from '@/query';
import { useParams } from 'react-router-dom';
import { newUid, parseFilter } from '@/util';
import { axios } from '@/axios';
import { config } from '@/config';
import { sectorsData } from '@/mocks/competencesOptionsData';
import PaginationNew from '@/components/DataTable/PaginationNew';
import EmptyTable from '@/components/DataTable/EmptyTable';
import { AuthContext } from '@/components/Contexts/AuthContext';

import TextWithTooltip from './TextWithTooltip';
import AddProfileModal from '@/components/Competencies/AddProfileModal';
import AddCompetenceProfileModal from '@/components/Competencies/AddCompetenceProfileModal';
import { Listbox, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import ComponentDeleteModal from '@/components/DeleteModal/ComponentDeleteModal';

const CompetencyProfiles = ({
  categories,
  setRefetchTable,
  refetchTable,
}: any) => {
  const { auth } = useContext(AuthContext);

  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenAddCompetence, setIsOpenAddCompetence] = useState(false);
  const [activeProfile, setActiveProfile] = useState(null);

  const authUser = auth?.user;
  const { t } = useTranslation();
  const { organisationId } = useParams();
  const [count, setCount] = useState(10);
  const [maxPage, setMaxPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('');
  const [sector, setSector] = useState<any>();
  const [result, setResult] = useState<any>(null);
  const [professions, setProfessions] = useState<any>(null);
  const [isRowOpen, setIsRowOpen] = useState(false);
  const [rowIndex, setRowIndex] = useState();
  const [selectedRow, setSelectedRow] = useState<Set<string | number>>(
    new Set<string | number>()
  );
  const [selectMode, setSelectMode] = useState(true);

  const [isOpenDelModal, setIsOpenDelModal] = useState(false);

  // const [refetchTable, setRefetchTable] = useState<string>(newUid(20));

  const showOptions = [
    {
      value: 10,
      label: '10',
    },
    {
      value: 20,
      label: '20',
    },
    {
      value: 40,
      label: '40',
    },
    {
      value: 80,
      label: '80',
    },
  ];

  const [showType, setShowType] = useState(showOptions[0]);

  const { refetch: refetchProfessions } = useGet('/professions', {
    organisationId: `${organisationId},OR,organisationId=null`,
    categoryId: sector?.id,
    lang: authUser?.lang,
    count,
    offset,
    sort: 'name',
    q: parseFilter(filter),
    onSuccess: (result: any) => {
      setResult(result || {});
    },
  });

  useEffect(() => {
    if (result) {
      setProfessions(result?.data || {});
      const contentRange = result?.headers?.['content-range']
        ?.split(' ')?.[1]
        ?.split('/');
      const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 1;
      setMaxPage(Math.ceil(maxCount / count));
    }
  }, [result]);

  const page = Math.floor(offset / count) + 1;

  useEffect(() => {
    refetchProfessions();
  }, [filter, refetchTable, offset]);

  if (!professions) return null;

  const handleItemSelect = (key: any) => {
    const newSelected = new Set(selectedRow);
    if (
      (newSelected.has(key) && selectMode) ||
      (!newSelected.has(key) && !selectMode)
    ) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedRow(newSelected);
  };

  const deleteCompetencyProfile = () => {
    [...selectedRow]?.map((professionId: any) => {
      axios.delete(`${config.backend}/profession/${professionId}`).then(() => {
        setSelectedRow(new Set<string | number>());
        setRefetchTable(newUid(20));
        setIsOpenDelModal(false);
      });
    });
  };

  const sectorValue = (id: any) => {
    if (id) {
      const sector = sectorsData.find((el) => el.id == id);
      if (sector) {
        return sector?.name;
      } else {
        return null;
      }
    } else {
      return null;
    }
  };
  // console.log(refetchTable);

  return (
    <>
      <div className="flex w-full flex-col sm:flex-row sm:items-center">
        <div className="py-8 pr-2 sm:w-1/2">
          <InputText
            value={filter}
            onChange={(ev) => {
              setOffset(0);
              setFilter(ev.target.value);
            }}
            Icon={Search}
            placeholder={t('pages.manage_competencies.search_placeholder_1')}
          />
        </div>

        <div className="relative z-[1] ml-auto flex flex-col justify-between lg:flex-row lg:items-center">
          <div className="flex flex-col sm:ml-auto sm:flex-row lg:mt-0">
            {selectedRow?.size > 0 && (
              <div className="mb-2 sm:mb-0">
                <Button
                  // onClick={deleteCompetencyProfile}
                  onClick={() => setIsOpenDelModal(true)}
                  size="sm"
                  disabled={selectedRow?.size < 1}
                  variant="outline"
                  className="!text-error-04"
                >
                  {t('pages.manage_competencies.delete_button')}
                </Button>
              </div>
            )}

            <div>
              <Button
                onClick={() => {
                  //   navigate(`${basePath}/addCompetencyProfile`);
                  // setIsOpenAdd(true);
                  document.body.style.overflow = 'hidden';
                  setIsOpenAddModal(true);
                }}
                size="sm"
                className="flex items-center sm:ml-2"
                variant="outline"
              >
                {t('pages.manage_competencies.add_competency_profile')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {professions?.length ? (
        <>
          <div key={refetchTable}>
            {professions?.map((profession: any) => (
              <div
                key={refetchTable + '_' + profession.id}
                className="mb-3 overflow-hidden rounded-xl border bg-grey-01"
              >
                <div className="flex items-center py-4 px-6">
                  <div className="flex items-center">
                    <Checkbox
                      disabled={
                        authUser?.activeOrganisationId !== 18 &&
                        authUser?.activeOrganisationId !==
                          profession?.organisationId
                      }
                      id="selectedRow"
                      ariaLabel="test"
                      onChange={() => handleItemSelect(profession.id)}
                      checked={selectedRow.has(profession.id)}
                      key={profession.id + '_' + selectedRow.has(profession.id)}
                    />
                    <span className="overflow-hidden text-ellipsis pl-6 font-medium sm:text-xs md:text-base lg:text-base">
                      {profession.name}
                    </span>
                  </div>

                  <div className="flex justify-center font-medium sm:text-xs md:text-base lg:text-base">
                    {sectorValue(profession?.categoryId)}
                  </div>

                  <div className="ml-auto flex items-center justify-end gap-2">
                    <IconButton
                      Icon={Pencil}
                      variant="ghost"
                      onClick={() => {
                        setActiveProfile(profession);
                        document.body.style.overflow = 'hidden';
                        setIsOpenEditModal(true);
                      }}
                      className="ml-2"
                      disabled={
                        authUser?.activeOrganisationId !== 18 &&
                        authUser?.activeOrganisationId !==
                          profession?.organisationId
                      }
                    />
                    <IconButton
                      Icon={Plus}
                      variant="ghost"
                      onClick={() => {
                        setActiveProfile(profession);
                        document.body.style.overflow = 'hidden';
                        setIsOpenAddCompetence(true);
                      }}
                      disabled={
                        authUser?.activeOrganisationId !== 18 &&
                        authUser?.activeOrganisationId !==
                          profession?.organisationId
                      }
                    />

                    <IconButton
                      Icon={
                        isRowOpen && rowIndex === profession.id
                          ? ChevronUp
                          : ChevronDown
                      }
                      variant="ghost"
                      onClick={() => {
                        setRowIndex(profession.id);
                        !isRowOpen && setIsRowOpen(!isRowOpen);
                        isRowOpen &&
                          profession.id == rowIndex &&
                          setIsRowOpen(!isRowOpen);
                      }}
                    />
                  </div>
                </div>

                {isRowOpen && rowIndex === profession.id && (
                  <div className="bg-white px-2 pt-2">
                    <div className="pb-2">
                      <DataTable
                        key={refetchTable}
                        fetch={useGet.bind(
                          null,
                          '/professioncompetencedetails',
                          {
                            professionId: profession.id,
                            lang: authUser?.lang,
                          }
                        )}
                        columns={[
                          {
                            addTitleOnMobile: true,
                            title: t('columns.code'),
                            id: 'code',
                            className:
                              'col-start-1 col-end-3 break-all row-start-2',
                            sortable: false,
                            classNameDesktop: 'w-[150px]',
                            render: (item) => {
                              return (
                                <p className="text-xs text-grey-06">
                                  {item?.code}
                                </p>
                              );
                            },
                          },
                          {
                            addTitleOnMobile: true,
                            title: t('columns.competence'),
                            id: 'competence',
                            className: 'col-start-1 col-end-10 row-start-1',
                            sortable: false,
                            render: (item) => {
                              return item.name;
                            },
                          },
                          {
                            addTitleOnMobile: true,
                            title: t('columns.category'),
                            id: 'category',
                            className:
                              'col-start-3 col-end-8 row-start-2 text-right justify-center',
                            sortable: false,
                            classNameDesktop: 'max-w-[350px]',
                            render: (item) => {
                              return (
                                <>
                                  {item?.competenceCategoryId ? (
                                    <p className="flex min-w-max items-center text-grey-08 sm:pr-3">
                                      <span
                                        style={{
                                          backgroundColor:
                                            item?.competenceCategoryColorCode,
                                        }}
                                        className={`mr-2 h-2 w-2 min-w-2 rounded-full`}
                                      ></span>
                                      {item?.competenceCategoryName}
                                    </p>
                                  ) : (
                                    <p className="flex min-w-max items-center text-xs text-grey-05 sm:pr-3">
                                      {t(
                                        'pages.manage_competencies.category_no'
                                      )}
                                    </p>
                                  )}
                                </>
                              );
                            },
                          },
                          {
                            addTitleOnMobile: true,
                            id: 'mandatory',
                            title: t('pages.create_courses.mandatory'),
                            sortable: false,
                            classNameDesktop: 'w-[130px]',
                            className:
                              'col-start-8 col-end-10 row-start-2 text-right justify-end',
                            align: 'right',
                            render: (item) => {
                              return (
                                <div>
                                  {item?.isMandatory ? (
                                    <Tag
                                      title={t('forms.yes')}
                                      variant="success"
                                    />
                                  ) : (
                                    <Tag
                                      title={t('forms.no')}
                                      variant="default"
                                    />
                                  )}
                                </div>
                              );
                            },
                          },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {maxPage > 1 && (
            <div className="flex w-full items-center justify-between">
              <div>
                <PaginationNew
                  setOffset={setOffset}
                  offset={offset}
                  count={count}
                  page={page}
                  maxPage={maxPage}
                />
              </div>
              <div className="flex items-center">
                <div className="ml-2 hidden items-center gap-1 lg:flex">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.go_to_page')}
                  </p>
                  <InputNumber
                    mode="integer"
                    value={page}
                    onChange={(value: any) => {
                      if (value > 0) {
                        setOffset((value - 1) * count);
                      }
                    }}
                    min={1}
                    max={maxPage}
                    className="max-w-[50px]"
                  />
                </div>
                <div className="ml-2 flex items-center gap-1">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.show')}
                  </p>
                  <div>
                    <Listbox
                      value={showType}
                      onChange={(newShowType) => {
                        setShowType(newShowType);
                        setCount(newShowType?.value);
                        setOffset(
                          Math.floor(offset / newShowType?.value) *
                            newShowType?.value
                        );
                      }}
                    >
                      {({ open }) => (
                        <div>
                          <Listbox.Button
                            className={clsx(
                              'flex items-center justify-between',
                              'rounded-md border border-solid outline outline-2',
                              'min-h-10 w-full min-w-[70px] px-2 text-base',
                              {
                                'border-secondary-04 bg-white outline-secondary-01':
                                  open,
                                'border-transparent bg-grey-transparent-01 text-grey-08 outline-transparent hover:border-grey-transparent-03 hover:bg-white hover:outline-grey-transparent-01 focus:border-secondary-04 focus:bg-white focus:outline-secondary-01':
                                  !open,
                              }
                            )}
                          >
                            <span className="mr-2 block truncate">
                              {!showType?.label ? '' : showType?.label}
                            </span>
                            <ChevronDownFilled
                              className={clsx(
                                'h-5 w-5',
                                'transition-transform',
                                open && 'rotate-180'
                              )}
                            />
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition-opacity"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <div className="listbox relative z-10">
                              <Listbox.Options className="absolute bottom-0 mt-1  flex max-h-40 w-full -translate-y-[42px] cursor-pointer flex-col gap-1 overflow-auto rounded-lg bg-white p-1 drop-shadow-s focus:outline-none">
                                {showOptions.map((option, optionIndex) => (
                                  <Listbox.Option
                                    key={optionIndex}
                                    className={({ active }) =>
                                      clsx(
                                        'rounded-md px-3 py-2 text-base text-black',
                                        active && 'bg-grey-transparent-02'
                                      )
                                    }
                                    value={option}
                                  >
                                    {({ selected }) => (
                                      <span
                                        className={clsx(
                                          'block',
                                          selected && 'font-medium'
                                        )}
                                      >
                                        <span>{option?.label}</span>
                                      </span>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Transition>
                        </div>
                      )}
                    </Listbox>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyTable
          template={2}
          text={t('empty_tenplate.profiles_list')}
          btn_text={t('empty_tenplate.btn_add_profile')}
          btnClick={() => {
            document.body.style.overflow = 'hidden';
            setIsOpenAddModal(true);
          }}
          filter={filter}
        />
      )}

      {isOpenDelModal && (
        <ComponentDeleteModal
          closeModal={() => setIsOpenDelModal(false)}
          setIsOpenDeleteModal={setIsOpenDelModal}
          title={t(
            selectedRow?.size > 1
              ? 'delete_modal.profiles.title_m'
              : 'delete_modal.profiles.title'
          )}
          heading={t(
            selectedRow?.size > 1
              ? 'delete_modal.profiles.heading_m'
              : 'delete_modal.profiles.heading'
          )}
          text={t(
            selectedRow?.size > 1
              ? 'delete_modal.profiles.text_m'
              : 'delete_modal.profiles.text'
          )}
          handleDelete={() => deleteCompetencyProfile()}
        />
      )}

      {isOpenAddModal && (
        <AddProfileModal
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsOpenAddModal(false);
          }}
          setIsOpenAddModal={setIsOpenAddModal}
          setRefetchTable={setRefetchTable}
          authUser={authUser}
          edit={false}
          setActiveProfile={null}
          activeProfile={null}
          categories={categories}
        />
      )}
      {isOpenEditModal && (
        <AddProfileModal
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsOpenEditModal(false);
          }}
          setIsOpenAddModal={setIsOpenEditModal}
          setRefetchTable={setRefetchTable}
          authUser={authUser}
          edit={true}
          setActiveProfile={setActiveProfile}
          activeProfile={activeProfile}
          categories={categories}
        />
      )}
      {isOpenAddCompetence && (
        <AddCompetenceProfileModal
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsOpenAddCompetence(false);
          }}
          setIsOpenAddModal={setIsOpenAddCompetence}
          setRefetchTable={setRefetchTable}
          authUser={authUser}
          setActiveProfile={setActiveProfile}
          activeProfile={activeProfile}
          categories={categories}
        />
      )}
    </>
  );
};

export default CompetencyProfiles;
